import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MailCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { toast } from "sonner";

export default function Verify() {
  const { verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!email) navigate("/register");
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    timer.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await verifyEmail(email, code.trim());
      toast.success("Email verified — welcome to the line.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await resendCode(email);
      toast.success("A new code is on its way.");
      setCooldown(30);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] text-white p-6 grid-lines">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-10">
          <img src="/rtb-logo.png?v=2" alt="Right To Bear" className="h-14 w-auto object-contain" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-2xl tracking-tight">RIGHT TO BEAR</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--rtb-copper))] font-medium mt-1">
              Pure Peace of Mind
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-2 overline text-[hsl(var(--primary))] mb-3">
          <MailCheck className="w-4 h-4" /> Verify Your Email
        </div>
        <h1 className="font-display text-5xl tracking-tight leading-none mb-4">CHECK YOUR INBOX</h1>
        <p className="text-white/55 text-sm leading-relaxed mb-8">
          We sent a 6-digit verification code to{" "}
          <span className="text-white font-medium">{email}</span>. Enter it below to activate your
          rep account.
        </p>

        {error && (
          <div data-testid="verify-error" className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm px-4 py-3 mb-5">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <input
            data-testid="verify-code"
            inputMode="numeric"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full bg-[#22303C] border border-white/15 px-4 py-4 text-white text-center text-3xl tracking-[0.6em] font-display focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            placeholder="000000"
          />
          <button
            data-testid="verify-submit"
            type="submit"
            disabled={busy || code.length < 6}
            className="w-full flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white overline py-4 hard-shadow-hover disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Verify & Enter"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            data-testid="verify-resend"
            onClick={resend}
            disabled={cooldown > 0}
            className="text-white/60 hover:text-white underline disabled:opacity-40 disabled:no-underline"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
          <Link to="/register" className="text-white/40 hover:text-white">
            Wrong email? Start over
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
